import type { PaymentSubState } from '@diamond/shared/types';
import { buyerCaptureException } from '@diamond/shared/utils';
import { isAxiosError } from 'axios';

import { formatPaymentSubStateLabel } from './format-payment-sub-state-label';
import { readApiErrorMessage } from './get-api-error-message';

export type PaymentInstructionSentrySource =
  | 'sticky'
  | 'checkout'
  | 'instruction'
  | 'my_order'
  | 'notification'
  | 'guard';

interface PaymentInstructionSentryContextValues
  extends Record<string, unknown> {
  api_message?: string;
  order_meta_id?: string;
  payment_sub_state?: string;
  reason: string;
  source: PaymentInstructionSentrySource;
  status_code?: number;
  warehouse?: string;
}

export interface PaymentInstructionSentryScope {
  setContext: (
    name: string,
    context: PaymentInstructionSentryContextValues
  ) => void;
  setTag: (key: string, value: string) => void;
}

export interface PaymentInstructionTelemetryContext {
  ffPaymentInstructionPage: boolean;
  orderMetaId: string;
  paymentMethod?: string | null;
  source: PaymentInstructionSentrySource;
  warehouse: string;
}

interface ApplyPaymentInstructionSentryContextArgs {
  apiMessage?: string;
  orderMetaId?: string;
  paymentSubState?: PaymentSubState;
  reason: string;
  source: PaymentInstructionSentrySource;
  statusCode?: number;
  warehouse?: string;
}

interface ApiFailureDetails {
  apiError?: string;
  apiMessage?: string;
  statusCode?: number;
}

function readApiFailureDetails(error: unknown): ApiFailureDetails {
  const apiMessage = readApiErrorMessage(error);
  if (!isAxiosError(error)) {
    return { apiMessage };
  }

  const statusCode = error.response?.status;
  const responseError = error.response?.data?.error;
  const apiError =
    typeof responseError === 'string' && responseError.trim().length > 0
      ? responseError.trim()
      : undefined;

  return {
    apiError,
    apiMessage,
    statusCode: typeof statusCode === 'number' ? statusCode : undefined,
  };
}

export const applyPaymentInstructionSentryContext = (
  scope: PaymentInstructionSentryScope,
  {
    apiMessage,
    orderMetaId,
    paymentSubState,
    reason,
    source,
    statusCode,
    warehouse,
  }: ApplyPaymentInstructionSentryContextArgs
) => {
  const context: PaymentInstructionSentryContextValues = { reason, source };

  if (orderMetaId) context.order_meta_id = orderMetaId;
  if (warehouse) context.warehouse = warehouse;
  if (apiMessage) context.api_message = apiMessage;
  if (statusCode !== undefined) context.status_code = statusCode;
  if (paymentSubState !== undefined) {
    context.payment_sub_state = formatPaymentSubStateLabel(paymentSubState);
  }

  scope.setTag('feature', 'payment_instruction');
  scope.setTag('source', source);
  scope.setTag('payment_instruction', reason);
  if (statusCode !== undefined) {
    scope.setTag('http_status', String(statusCode));
  }
  scope.setContext('payment_instruction', context);
};

export const capturePaymentInstructionFailure = (
  error: unknown,
  context: PaymentInstructionTelemetryContext,
  reason: string,
  paymentSubState?: PaymentSubState
) => {
  const { apiError, apiMessage, statusCode } = readApiFailureDetails(error);

  buyerCaptureException(
    {
      error: error instanceof Error ? error.message : String(error),
      message: 'Payment instruction flow failed',
      reason,
      ...(statusCode !== undefined ? { status_code: statusCode } : {}),
      ...(apiMessage ? { api_message: apiMessage } : {}),
      ...(apiError ? { api_error: apiError } : {}),
    },
    (scope) => {
      applyPaymentInstructionSentryContext(scope, {
        apiMessage,
        orderMetaId: context.orderMetaId,
        paymentSubState,
        reason,
        source: context.source,
        statusCode,
        warehouse: context.warehouse,
      });
    }
  );
};
